import React from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { isMono, isColor, isPhoto } from "../log/logLabels";
import { saveXlsx } from "../../utils/xlsx";

// A printer's income comes as the payments of each color type, and of each photo_type
// when they were photo orders. The kiosk charges those as colored pages, so they are
// photos first and leave the colored column to documents.
const isMonoDocs = (amount) => !isPhoto(amount) && isMono(amount.color_type);
const isColorDocs = (amount) => !isPhoto(amount) && isColor(amount.color_type);
const isIdPhoto = (amount) => amount.photo_type === "id";

// A sum over the paid payments: "totalAmount" (income), "payments" (how many) or
// "pages" (pages × copies), of every kind without a test
const sumOf = (item, field, test) =>
  (item.totalAmount || [])
    .filter((amount) => !test || test(amount))
    .reduce((sum, amount) => sum + Number(amount[field] || 0), 0);

const formatMoney = (value) => `${new Intl.NumberFormat("mn-MN").format(value)} ₮`;
const formatCount = (value) => new Intl.NumberFormat("mn-MN").format(value);

// The table's columns in Excel, the pages and the photos of each kind a column of their
// own: the row's field of each, or how a printer's text comes from the row
const SHEET_COLUMNS = [
  { label: "Киоск", width: 24, text: (row) => row.item.printerDetails?.name || "Нэргүй киоск" },
  { label: "Байршил", width: 32, text: (row) => row.item.printerDetails?.location || "" },
  { label: "Гүйлгээ", field: "payments", format: "count", width: 10 },
  { label: "Хуудас", field: "pages", format: "count", width: 10 },
  { label: "Хар цагаан хуудас", field: "monoPages", format: "count", width: 12 },
  { label: "Өнгөт хуудас", field: "colorPages", format: "count", width: 10 },
  { label: "Зургийн хуудас", field: "photoPages", format: "count", width: 10 },
  { label: "Хар цагаан", field: "mono", format: "money" },
  { label: "Өнгөт", field: "color", format: "money" },
  { label: "Зураг", field: "photo", format: "money" },
  { label: "Үүнээс 10×15", field: "printPhoto", format: "money" },
  { label: "Үүнээс цээж зураг", field: "idPhoto", format: "money" },
  { label: "Нийт орлого", field: "total", format: "money", width: 16 },
];

const dayOf = (date) => format(new Date(date), "yyyy-MM-dd");

const PrinterIncomeTable = ({ printerData, range }) => {
  const rows = (printerData || [])
    .map((item) => ({
      item,
      mono: sumOf(item, "totalAmount", isMonoDocs),
      color: sumOf(item, "totalAmount", isColorDocs),
      photo: sumOf(item, "totalAmount", isPhoto),
      idPhoto: sumOf(item, "totalAmount", isIdPhoto),
      printPhoto: sumOf(item, "totalAmount", (amount) => isPhoto(amount) && !isIdPhoto(amount)),
      total: sumOf(item, "totalAmount"),
      payments: sumOf(item, "payments"),
      pages: sumOf(item, "pages"),
      monoPages: sumOf(item, "pages", isMonoDocs),
      colorPages: sumOf(item, "pages", isColorDocs),
      photoPages: sumOf(item, "pages", isPhoto),
    }))
    .sort((a, b) => b.total - a.total);
  const grand = (field) => rows.reduce((sum, row) => sum + row[field], 0);

  // The rows as an Excel file, with the range they are of
  const download = () => {
    const start = dayOf(range?.startDate || new Date());
    const end = dayOf(range?.endDate || new Date());
    const days = start === end ? start : `${start} — ${end}`;
    saveXlsx(`orlogo_printer_${start}_${end}.xlsx`, {
      name: "Киоскийн орлого",
      title: `Киоскийн орлого · ${range?.label || "Өнөөдөр"} · ${days}`,
      columns: SHEET_COLUMNS,
      rows: rows.map((row) =>
        SHEET_COLUMNS.map((column) => (column.field ? row[column.field] : column.text(row)))
      ),
      total: SHEET_COLUMNS.map((column, index) =>
        column.field ? grand(column.field) : index === 0 ? "Нийт" : ""
      ),
    });
  };

  return (
    <section className="overflow-hidden bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Дэлгэрэнгүй</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Орлогоор эрэмбэлсэн киоскууд</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full dark:text-purple-200 dark:bg-purple-700">
            {rows.length} киоск
          </span>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center h-9 px-3 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              <i className="mr-2 fas fa-file-excel" />
              Excel татах
            </button>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-10 text-sm text-center text-gray-400">Харуулах мэдээлэл алга.</p>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full whitespace-no-wrap">
            <thead>
              <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase bg-gray-50 dark:text-gray-400 dark:bg-gray-900">
                <th className="px-5 py-3">Киоск</th>
                <th className="px-5 py-3 text-right">Гүйлгээ</th>
                <th className="px-5 py-3 text-right">Хуудас</th>
                <th className="px-5 py-3 text-right">Хар цагаан</th>
                <th className="px-5 py-3 text-right">Өнгөт</th>
                <th className="px-5 py-3 text-right">Зураг</th>
                <th className="px-5 py-3 text-right">Нийт орлого</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map(({ item, mono, color, photo, idPhoto, total, payments, pages, monoPages, colorPages, photoPages }, index) => (
                <tr key={item.printerDetails?._id || `${item.printerDetails?.name}-${index}`} className="text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-800 dark:text-gray-100">{item.printerDetails?.name || "Нэргүй киоск"}</p>
                    <p className="mt-1 text-xs text-gray-400">{item.printerDetails?.location || "Байршилгүй"}</p>
                  </td>
                  <td className="px-5 py-4 text-right">{formatCount(payments)}</td>
                  <td className="px-5 py-4 text-right">
                    {formatCount(pages)}
                    <p className="mt-1 text-xs text-gray-400">
                      хар {formatCount(monoPages)} · өнгөт {formatCount(colorPages)}
                      {photoPages > 0 && ` · зураг ${formatCount(photoPages)}`}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-right">{formatMoney(mono)}</td>
                  <td className="px-5 py-4 text-right">{formatMoney(color)}</td>
                  <td className="px-5 py-4 text-right">
                    {formatMoney(photo)}
                    {idPhoto > 0 && (
                      <p className="mt-1 text-xs text-gray-400">
                        10×15 {formatMoney(photo - idPhoto)} · цээж {formatMoney(idPhoto)}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 font-semibold text-right text-gray-900 dark:text-white">{formatMoney(total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold text-gray-800 bg-gray-50 dark:text-gray-100 dark:bg-gray-900">
                <td className="px-5 py-4">Нийт</td>
                <td className="px-5 py-4 text-right">{formatCount(grand("payments"))}</td>
                <td className="px-5 py-4 text-right">{formatCount(grand("pages"))}</td>
                <td className="px-5 py-4 text-right">{formatMoney(grand("mono"))}</td>
                <td className="px-5 py-4 text-right">{formatMoney(grand("color"))}</td>
                <td className="px-5 py-4 text-right">{formatMoney(grand("photo"))}</td>
                <td className="px-5 py-4 text-right text-purple-700 dark:text-purple-300">{formatMoney(grand("total"))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
};

PrinterIncomeTable.propTypes = {
  printerData: PropTypes.array,
  // The range the data is of: { startDate, endDate, label }
  range: PropTypes.object,
};
PrinterIncomeTable.defaultProps = { printerData: [], range: null };

export default PrinterIncomeTable;
